export type StartupDiagnosticEntry = {
  sequence: number;
  elapsedMs: number;
  timestamp: string;
  event: string;
  source: string;
  details?: Record<string, unknown>;
  stack?: string;
};

const STORAGE_KEY = "taskveo_startup_diagnostics";
const EVENT_NAME = "taskveo:startup-diagnostic";
const MAX_ENTRIES = 250;
const startedAt = performance.now();
let installed = false;
let sequence = 0;

const SENSITIVE_KEYS = /^(token|token_hash|access_token|refresh_token|code|id_token)$/i;

const sanitizeUrl = (value: string): string => {
  try {
    const url = new URL(value, window.location.origin);
    url.searchParams.forEach((paramValue, key) => {
      if (SENSITIVE_KEYS.test(key)) {
        url.searchParams.set(key, paramValue ? "[modtaget]" : "[tom]");
      }
    });
    if (url.hash) {
      const hash = new URLSearchParams(url.hash.replace(/^#/, ""));
      hash.forEach((paramValue, key) => {
        if (SENSITIVE_KEYS.test(key)) hash.set(key, paramValue ? "[modtaget]" : "[tom]");
      });
      url.hash = hash.toString();
    }
    return url.toString();
  } catch {
    return value.replace(/((?:token_hash|access_token|refresh_token|code|id_token)=)[^&#]*/gi, "$1[modtaget]");
  }
};

const sanitizeDetails = (details?: Record<string, unknown>): Record<string, unknown> | undefined => {
  if (!details) return undefined;
  return Object.fromEntries(
    Object.entries(details).map(([key, value]) => {
      if (SENSITIVE_KEYS.test(key)) return [key, value ? "[modtaget]" : "[tom]"];
      if (
        typeof value === "string" &&
        (key.toLowerCase().includes("url") || key === "from" || key === "to" || key === "route")
      ) {
        return [key, sanitizeUrl(value)];
      }
      return [key, value];
    }),
  );
};

let cachedEntries: StartupDiagnosticEntry[] | null = null;
let persistTimer: number | null = null;

const readEntries = (): StartupDiagnosticEntry[] => {
  if (cachedEntries) return cachedEntries;
  try {
    const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]") as StartupDiagnosticEntry[];
    cachedEntries = Array.isArray(parsed) ? parsed : [];
  } catch {
    cachedEntries = [];
  }
  return cachedEntries;
};

const flushEntries = () => {
  persistTimer = null;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cachedEntries ?? []));
  } catch {
    // Diagnostik må aldrig påvirke appens startup.
  }
};

/**
 * Diagnostikken skrives batchet. Tidligere blev hele listen (op til 250 poster
 * med stack traces) parset og serialiseret synkront ved HVER hændelse, hvilket
 * gav mærkbare hak/frys i Android-webviewen under navigation.
 */
const persistEntry = (entry: StartupDiagnosticEntry) => {
  const entries = readEntries();
  entries.push(entry);
  if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES);
  if (persistTimer !== null) return;
  try {
    persistTimer = window.setTimeout(flushEntries, 400);
  } catch {
    flushEntries();
  }
};

try {
  window.addEventListener("pagehide", () => {
    if (persistTimer !== null) {
      clearTimeout(persistTimer);
      flushEntries();
    }
  });
} catch {
  // ignore
}

const captureStack = (): string | undefined => {
  const stack = new Error("navigation trace").stack;
  if (!stack) return undefined;
  return stack
    .split("\n")
    .filter((line) => !line.includes("startupDiagnostics"))
    .slice(1, 16)
    .join("\n");
};

const IGNORED_FRAME = /startupDiagnostics|captureStack|navigation trace|react-dom|scheduler|node_modules\/react/;

/**
 * Udleder den mest sandsynlige "skyldige" frame (komponent + fil:linje) fra
 * stack tracet, så loggen peger på præcis kode i stedet for en hypotese.
 */
const captureCulprit = (): string | undefined => {
  const stack = new Error("navigation trace").stack;
  if (!stack) return undefined;
  const frames = stack
    .split("\n")
    .slice(1)
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && !IGNORED_FRAME.test(line));
  return frames[0];
};

const isHomeUrl = (value: string): boolean => {
  try {
    const url = new URL(value, window.location.origin);
    return url.pathname === "/" || url.pathname === "";
  } catch {
    return value === "/" || value === "";
  }
};

export const recordStartupDiagnostic = (
  event: string,
  source: string,
  details?: Record<string, unknown>,
  includeStack = false,
) => {
  sequence += 1;
  const entry: StartupDiagnosticEntry = {
    sequence,
    elapsedMs: Math.round(performance.now() - startedAt),
    timestamp: new Date().toISOString(),
    event,
    source,
    details: sanitizeDetails(details),
    stack: includeStack ? captureStack() : undefined,
  };
  persistEntry(entry);
  console.info("[StartupDiagnostics]", entry);
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: entry }));
};

/**
 * Registrerer en navigation og markerer den eksplicit, hvis destinationen er
 * forsiden, sammen med den konkrete frame der udførte navigationen.
 */
const recordNavigation = (event: string, source: string, from: string, to: string) => {
  const home = isHomeUrl(to);
  recordStartupDiagnostic(
    home ? `⛔ NAVIGATION TIL FORSIDEN (/) — ${event}` : event,
    source,
    {
      from,
      to,
      culprit: captureCulprit() ?? "ukendt frame",
      isHomeNavigation: home,
    },
    true,
  );
};

export const getStartupDiagnostics = (): StartupDiagnosticEntry[] => readEntries();

export const clearStartupDiagnostics = () => {
  try {
    cachedEntries = [];
    if (persistTimer !== null) {
      clearTimeout(persistTimer);
      persistTimer = null;
    }
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  window.dispatchEvent(new CustomEvent(EVENT_NAME));
};

export const formatStartupDiagnostics = (providedEntries?: StartupDiagnosticEntry[]): string =>
  (providedEntries ?? getStartupDiagnostics())
    .map((entry) => {
      const details = entry.details ? `\n${JSON.stringify(entry.details, null, 2)}` : "";
      const stack = entry.stack ? `\nStack:\n${entry.stack}` : "";
      return `#${entry.sequence} +${entry.elapsedMs}ms ${entry.event} — ${entry.source}${details}${stack}`;
    })
    .join("\n\n");

export const startupDiagnosticsEventName = EVENT_NAME;

export const installStartupDiagnostics = () => {
  if (installed) return;
  installed = true;

  const initialUrl = window.location.href;
  const params = new URL(initialUrl).searchParams;
  recordStartupDiagnostic("ANDROID/WEBVIEW URL MODTAGET", "main.tsx module startup", {
    url: initialUrl,
    pathname: window.location.pathname,
    queryKeys: Array.from(params.keys()),
    referrer: document.referrer || null,
    userAgent: navigator.userAgent,
    navigationType: performance.getEntriesByType("navigation")[0]
      ? (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming).type
      : "unknown",
  });

  const originalPushState = window.history.pushState.bind(window.history);
  const originalReplaceState = window.history.replaceState.bind(window.history);

  window.history.pushState = function pushState(data, unused, url) {
    recordNavigation(
      "HISTORY PUSH",
      "history.pushState caller",
      window.location.href,
      url === undefined || url === null ? window.location.href : String(url),
    );
    return originalPushState(data, unused, url);
  };

  window.history.replaceState = function replaceState(data, unused, url) {
    recordNavigation(
      "HISTORY REPLACE",
      "history.replaceState caller",
      window.location.href,
      url === undefined || url === null ? window.location.href : String(url),
    );
    return originalReplaceState(data, unused, url);
  };

  window.addEventListener("popstate", () => {
    recordStartupDiagnostic("POPSTATE", "window", { url: window.location.href });
  });
  window.addEventListener("hashchange", () => {
    recordStartupDiagnostic("HASHCHANGE", "window", { url: window.location.href });
  });
  window.addEventListener("pageshow", (event) => {
    recordStartupDiagnostic("WEBVIEW PAGE SHOWN", "window.pageshow", {
      url: window.location.href,
      persisted: event.persisted,
    });
  });
  window.addEventListener("pagehide", () => {
    recordStartupDiagnostic("WEBVIEW PAGE HIDDEN", "window.pagehide", { url: window.location.href });
  });

  // Fulde webview-loads (Median-handoff, intent://, eksterne redirects).
  try {
    const originalAssign = window.location.assign.bind(window.location);
    const originalLocationReplace = window.location.replace.bind(window.location);
    window.location.assign = (url: string | URL) => {
      recordNavigation("LOCATION.ASSIGN (fuldt load)", "window.location.assign caller", window.location.href, String(url));
      return originalAssign(url as string);
    };
    window.location.replace = (url: string | URL) => {
      recordNavigation("LOCATION.REPLACE (fuldt load)", "window.location.replace caller", window.location.href, String(url));
      return originalLocationReplace(url as string);
    };
  } catch {
    // Nogle webviews tillader ikke patching af location — diagnostik må aldrig fejle.
  }

  // Median-broen injiceres asynkront: log præcis hvornår den bliver klar, så vi
  // kan se om app-shell-detektionen var tilgængelig da beslutningen blev truffet.
  const medianWindow = window as Window & { median?: unknown; gonative?: unknown };
  const medianStart = performance.now();
  const medianTimer = window.setInterval(() => {
    const ready = Boolean(medianWindow.median || medianWindow.gonative);
    const timedOut = performance.now() - medianStart > 5000;
    if (!ready && !timedOut) return;
    window.clearInterval(medianTimer);
    recordStartupDiagnostic(
      ready ? "MEDIAN-BRO KLAR" : "MEDIAN-BRO IKKE FUNDET (5s timeout)",
      "startupDiagnostics median probe",
      {
        bridgeReady: ready,
        hasMedian: Boolean(medianWindow.median),
        hasGonative: Boolean(medianWindow.gonative),
        url: window.location.href,
      },
    );
  }, 100);

  window.addEventListener("beforeunload", () => {
    recordStartupDiagnostic("WEBVIEW FORLADER SIDEN", "window.beforeunload", { url: window.location.href }, true);
  });
};